import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { IntakeHubDocumentItemLinkResponse } from '@store/generated/intake-hub';

import type { IntakeDocumentDetail } from './intakeDocumentDetail';
import { IntakeStatusChip } from './IntakeStatusChip';

interface IntakeDocumentDetailDialogProps {
  open: boolean;
  document: IntakeDocumentDetail | null;
  previewUrl: string | null;
  isPreviewLoading: boolean;
  onOpenFile: (sourceArtifactId?: string | null) => Promise<void>;
  onClose: () => void;
}

function humanize(value?: string | null): string {
  if (!value) return 'Document';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(stringifyValue).join(', ');
  return JSON.stringify(value);
}

export function IntakeDocumentDetailDialog({
  open,
  document,
  previewUrl,
  isPreviewLoading,
  onOpenFile,
  onClose,
}: IntakeDocumentDetailDialogProps) {
  if (!document) return null;

  const fields = document.extractedFields ?? [];
  const suggestedFields = document.suggestedFields ?? [];
  const linkedItems = document.linkedItems ?? [];
  const dateLabel = formatDate(document.createdAt);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      sx={{
        '& .MuiDialog-paper': {
          height: { xs: '96vh', md: '90vh' },
          borderRadius: { xs: 0, md: '8px' },
          overflow: 'hidden',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${ELIG_TOKENS.line}`,
        }}
      >
        <DescriptionOutlinedIcon sx={{ color: ELIG_TOKENS.midnight, fontSize: 20 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" noWrap sx={{ color: ELIG_TOKENS.ink, fontWeight: 800 }}>
            {document.title}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {document.secondaryName && (
              <Typography variant="caption" noWrap sx={{ color: ELIG_TOKENS.inkFaint }}>
                {document.secondaryName}
              </Typography>
            )}
            {document.docKind && <Chip size="small" label={humanize(document.docKind)} />}
            {document.documentDomain && (
              <Chip size="small" variant="outlined" label={humanize(document.documentDomain)} />
            )}
            {dateLabel && (
              <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                Uploaded {dateLabel}
              </Typography>
            )}
          </Stack>
        </Box>
        <Button
          size="small"
          variant="text"
          endIcon={<OpenInNewOutlinedIcon fontSize="small" />}
          onClick={() => void onOpenFile(document.sourceArtifactId)}
          sx={{ color: ELIG_TOKENS.midnight, textTransform: 'none', fontWeight: 700 }}
        >
          Open file
        </Button>
        <IconButton size="small" aria-label="Close document details" onClick={onClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.35fr) minmax(360px, 0.65fr)' },
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              minHeight: { xs: 360, md: 0 },
              bgcolor: '#F4F5F7',
              p: { xs: 1, md: 1.5 },
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            <DocumentPreview
              title={document.title}
              previewUrl={previewUrl}
              isPreviewLoading={isPreviewLoading}
              sourceArtifactId={document.sourceArtifactId}
              onOpenFile={onOpenFile}
            />
          </Box>

          <Stack
            spacing={2}
            sx={{
              minHeight: 0,
              overflow: 'auto',
              p: 2,
              borderLeft: { md: `1px solid ${ELIG_TOKENS.line}` },
              bgcolor: ELIG_TOKENS.card,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: ELIG_TOKENS.midnight, fontWeight: 800 }}>
                Information pulled from this document
              </Typography>
              {fields.length > 0 || suggestedFields.length > 0 ? (
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {fields.map(field => (
                    <Box
                      key={`${document.id}-${field.source_intake_item_id}-${field.key}`}
                      sx={{
                        borderRadius: '6px',
                        bgcolor: 'rgba(28,30,133,0.025)',
                        border: `1px solid ${ELIG_TOKENS.line}`,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
                        {field.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: ELIG_TOKENS.ink, fontWeight: 750, wordBreak: 'break-word' }}
                      >
                        {stringifyValue(field.value)}
                      </Typography>
                    </Box>
                  ))}
                  {suggestedFields.map(({ label, suggestion }) => (
                    <Box
                      key={`${document.id}-${suggestion.extraction_id}-${suggestion.field_id}`}
                      sx={{
                        borderRadius: '6px',
                        bgcolor: 'rgba(28,30,133,0.025)',
                        border: `1px solid ${ELIG_TOKENS.line}`,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
                          {label}
                        </Typography>
                        <Chip size="small" variant="outlined" label="Suggested" />
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ color: ELIG_TOKENS.ink, fontWeight: 750, wordBreak: 'break-word' }}
                      >
                        {stringifyValue(suggestion.value)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft, mt: 1 }}>
                  No intake information has been matched from this document yet.
                </Typography>
              )}
            </Box>

            {linkedItems.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: ELIG_TOKENS.midnight, fontWeight: 800 }}
                >
                  Matched intake items
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {linkedItems.map(item => (
                    <LinkedItemRow
                      key={`${document.id}-${item.intake_item_id ?? item.requirement_key}`}
                      item={item}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function DocumentPreview({
  title,
  previewUrl,
  isPreviewLoading,
  sourceArtifactId,
  onOpenFile,
}: {
  title: string;
  previewUrl: string | null;
  isPreviewLoading: boolean;
  sourceArtifactId?: string | null;
  onOpenFile: (sourceArtifactId?: string | null) => Promise<void>;
}) {
  if (isPreviewLoading) {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (previewUrl) {
    return (
      <Box
        component="iframe"
        src={previewUrl}
        title={title}
        sx={{
          width: '100%',
          height: '100%',
          border: 0,
          borderRadius: '6px',
          bgcolor: ELIG_TOKENS.card,
        }}
      />
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        color: ELIG_TOKENS.inkSoft,
        textAlign: 'center',
      }}
    >
      <DescriptionOutlinedIcon sx={{ fontSize: 54, color: ELIG_TOKENS.inkFaint }} />
      <Typography variant="body2">Preview is not available for this document.</Typography>
      <Button
        size="small"
        variant="contained"
        disabled={!sourceArtifactId}
        onClick={() => void onOpenFile(sourceArtifactId)}
        sx={{
          bgcolor: ELIG_TOKENS.midnight,
          textTransform: 'none',
          '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
        }}
      >
        Open file
      </Button>
    </Stack>
  );
}

function LinkedItemRow({ item }: { item: IntakeHubDocumentItemLinkResponse }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: '6px',
        border: `1px solid ${ELIG_TOKENS.line}`,
        px: 1,
        py: 0.75,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 750 }} noWrap>
          {item.title || humanize(item.requirement_key)}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {humanize(item.item_type)}
        </Typography>
      </Box>
      <IntakeStatusChip status={item.status} />
    </Stack>
  );
}
